import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';
import { MicButton } from './MicButton.js';

export function InlineNoteEditor() {
  const id = useStore((s) => s.noteEditingId);
  const setId = useStore((s) => s.setNoteEditingId);
  const updateNode = useStore((s) => s.updateNode);
  const setSelected = useStore((s) => s.setSelected);
  const setEditPanelOpen = useStore((s) => s.setEditPanelOpen);
  const enterUniverse = useStore((s) => s.enterUniverse);
  // Re-render when zoom changes so font size scaling stays in sync (RAF handles xy).
  const zoom = useStore((s) => s.cam.zoom);

  const initialNode = id ? useStore.getState().nodes.find((n) => n.id === id) ?? null : null;
  const [draft, setDraft] = useState('');
  const draftRef = useRef('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const originalRef = useRef('');
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!id) return;
    const node = useStore.getState().nodes.find((n) => n.id === id);
    if (!node) return;
    const initial = typeof node.content === 'string' ? node.content : '';
    originalRef.current = initial;
    draftRef.current = initial;
    setDraft(initial);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [id]);

  useEffect(() => {
    if (!id) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target as Node)) return;
      commit();
      setId(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setId(null);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Position the card synchronously before first paint to avoid the flash at (0,0).
  // Then RAF keeps it glued to the node through physics motion.
  useLayoutEffect(() => {
    if (!id) return;
    function position() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const st = useStore.getState();
      const node = st.nodes.find((n) => n.id === id);
      if (!node) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const sx = (node.x + st.cam.x) * st.cam.zoom + w / 2;
      const sy = (node.y + st.cam.y) * st.cam.zoom + h / 2;
      const cardW = wrap.offsetWidth;
      const cardH = wrap.offsetHeight;
      let left = sx - cardW / 2;
      let top = sy - cardH / 2;
      if (left + cardW > w - 8) left = w - cardW - 8;
      if (left < 8) left = 8;
      if (top + cardH > h - 8) top = h - cardH - 8;
      if (top < 60) top = 60;
      wrap.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
      // Publish bounds so the canvas renderer can mask link segments behind the card.
      useStore.getState().setNoteCardBounds({
        left: Math.round(left), top: Math.round(top),
        width: Math.round(cardW), height: Math.round(cardH),
      });
    }
    // Sync placement before browser paints — eliminates the (0,0) flash.
    position();
    function step() {
      position();
      rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      useStore.getState().setNoteCardBounds(null);
    };
  }, [id]);

  function commit() {
    if (!id) return;
    const node = useStore.getState().nodes.find((n) => n.id === id);
    if (!node) return;
    // Read from ref to avoid stale closure inside document-level listeners.
    if (draftRef.current === originalRef.current) return;
    updateNode(id, { content: draftRef.current });
    originalRef.current = draftRef.current;
  }

  if (!id || !initialNode) return null;

  const color = initialNode.color || '#00f5ff';
  const isImage = initialNode.type === 'image';
  // Card scales subtly with node size so giant nodes get proportional popups,
  // but stays compact (smaller than before) so it feels like part of the node.
  // Card replaces node. Title sits above the card; card itself has organic blob border.
  const baseR = (initialNode.size || 22) * zoom;
  const cardW = Math.max(220, Math.min(340, baseR * 8));
  const titleFont = Math.max(13, Math.min(28, baseR * 0.9));

  return (
    <div
      ref={wrapRef}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        commit();
        setId(null);
        setSelected(id);
        setEditPanelOpen(true);
      }}
      onDoubleClick={(e) => {
        // Treat double-click anywhere outside the textarea as "enter universe"
        // (mirrors canvas double-click on the node).
        const target = e.target as HTMLElement;
        if (target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        e.stopPropagation();
        commit();
        setId(null);
        enterUniverse(id);
      }}
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: cardW,
        zIndex: 220,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: 0,
        opacity: 0,
        animation: 'note-fade-in .14s ease-out forwards',
        willChange: 'transform, opacity',
        pointerEvents: 'auto',
      }}
    >
      {/* Title — above the card, mirrors how labels render below ordinary nodes. */}
      <div style={{
        fontSize: titleFont,
        fontWeight: 700,
        color: 'var(--text)',
        letterSpacing: '-0.01em',
        textShadow: `0 0 14px ${color}aa, 0 0 4px ${color}66`,
        textAlign: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '0 6px',
      }}>{initialNode.label || 'Sem título'}</div>

      {/* Card — neon-glowing organic border + halo to feel like the node body. */}
      <div style={{
        position: 'relative',
        width: '100%',
        background: 'rgba(4, 10, 18, 0.7)',
        border: `1.6px solid ${color}cc`,
        borderRadius: 22,
        boxShadow: `0 0 0 1px ${color}33, 0 0 22px ${color}55, 0 0 48px ${color}33, inset 0 0 18px ${color}22`,
        backdropFilter: 'blur(12px)',
        padding: '8px 10px 10px',
        overflow: 'hidden',
      }}>
        <button
          onClick={() => { commit(); setId(null); }}
          aria-label="Fechar anotação"
          style={{
            position: 'absolute', top: 4, right: 6,
            background: 'transparent', border: 'none', color: `${color}cc`,
            cursor: 'pointer', fontSize: 15, padding: 2, lineHeight: 1,
            fontWeight: 300,
          }}
        >×</button>
        {!isImage && (
          <span
            style={{ position: 'absolute', top: 2, left: 6 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <MicButton
              size={14}
              onText={(t) => {
                const next = (draftRef.current ? draftRef.current.trimEnd() + ' ' : '') + t;
                draftRef.current = next;
                setDraft(next);
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
            />
          </span>
        )}
        {isImage ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', padding: '14px 4px 4px', lineHeight: 1.5 }}>
            Use o painel (botão direito) para alterar o asset.
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => { draftRef.current = e.target.value; setDraft(e.target.value); }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                commit();
                setId(null);
              }
            }}
            placeholder="Anote aqui…"
            rows={3}
            style={{
              width: '100%', resize: 'none', minHeight: 70,
              marginTop: 14,
              background: `${color}0d`,
              border: `1px solid ${color}55`,
              borderRadius: 10,
              padding: '8px 10px',
              color,
              fontSize: 13, lineHeight: 1.55, fontWeight: 500,
              letterSpacing: '-0.005em',
              textShadow: `0 0 8px ${color}55`,
              caretColor: color,
              fontFamily: 'inherit', outline: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
}
